import { v4 as uuidv4 } from 'uuid';

/**
 * This browser's device id. Kept across sign-outs on purpose: the API uses it
 * to recognise a device the team already knows (its name, its session) and to
 * move a user's seat here instead of taking a new one.
 */
const DEVICE_ID_KEY = 'ardealy.bo.device.id';

export function getDeviceId(): string {
  try {
    let id = localStorage.getItem(DEVICE_ID_KEY);
    if (!id) {
      id = uuidv4();
      localStorage.setItem(DEVICE_ID_KEY, id);
    }
    return id;
  } catch {
    // Storage blocked (private mode): the API treats the device as new each time.
    return 'no-storage';
  }
}

/** Why the API ended this device's session. Mirrors SESSION_END_REASONS in the API. */
export type SessionEndReason =
  | 'signed_out'
  | 'replaced'
  | 'revoked_by_vendor'
  | 'revoked_by_admin'
  | 'seat_taken'
  | 'password_changed'
  | 'missing';

export interface SignoutNotice {
  reason: SessionEndReason;
  at: number;
}

/**
 * Read by the login page to explain the sign-out. Session storage: it belongs
 * to this tab, and a fresh visit shouldn't show a stale "you were signed out".
 */
const NOTICE_KEY = 'ardealy.bo.signout.notice';

/** Fired when the API says this device may no longer be signed in. */
export const SESSION_ENDED_EVENT = 'ardealy:session-ended';

export function endSessionLocally(reason: SessionEndReason): void {
  try {
    const notice: SignoutNotice = { reason, at: Date.now() };
    sessionStorage.setItem(NOTICE_KEY, JSON.stringify(notice));
  } catch {
    // ignore
  }
  window.dispatchEvent(new CustomEvent(SESSION_ENDED_EVENT, { detail: { reason } }));
}

export function takeSignoutNotice(): SignoutNotice | null {
  try {
    const raw = sessionStorage.getItem(NOTICE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(NOTICE_KEY);
    const notice = JSON.parse(raw) as SignoutNotice;
    // Only explain a sign-out that just happened.
    return Date.now() - notice.at < 10 * 60_000 ? notice : null;
  } catch {
    return null;
  }
}

/** The API's explanation when a device can't sign in or was signed out. */
export interface SessionErrorBody {
  code?: 'SESSION_ENDED' | string;
  reason?: SessionEndReason;
  message?: string;
}

/** Read a failed request's body, whether it arrived as a Response or a normalised Error. */
export async function readSessionError(e: any): Promise<SessionErrorBody | null> {
  if (!e) return null;
  // A Response first: it has a `.body` of its own (the byte stream), which is
  // not the parsed error.
  if (typeof Response !== 'undefined' && e instanceof Response) {
    try {
      return await e.clone().json();
    } catch {
      return null;
    }
  }
  if (e.body && typeof e.body === 'object') return e.body;
  return null;
}
